import { useState } from 'react';
import { useClient } from 'hooks/useClient';
import { useForm } from 'hooks/useForm';
import { toast } from '@/hooks/use-toast';

export type EmailerFormData = {
  subject: string;
  greeting?: string;
  content: string;
  sendTo?: {
    everyone?: boolean;
    owners?: boolean;
    admins?: boolean;
    excludeConverdiant?: boolean;
  };
};

export type SendProgress = {
  sent: number;
  total: number;
  status: 'idle' | 'sending' | 'complete' | 'error';
  failed?: number;
  failedEmails?: string[];
};

const initialState: EmailerFormData = {
  subject: '',
  greeting: '',
  content: '',
  sendTo: {
    everyone: false,
    owners: false,
    admins: false,
    excludeConverdiant: false,
  },
};

const initialProgress: SendProgress = {
  sent: 0,
  total: 0,
  status: 'idle',
};

export const useEmailer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<SendProgress>(initialProgress);
  const apiClient = useClient();
  const formManager = useForm<EmailerFormData>(initialState);
  const { state: formState } = formManager;

  const getRecipientCount = async () => {
    try {
      const response = await apiClient.post('/emails/recipient-count', {
        sendTo: formState.sendTo,
      });
      return response.recipientCount;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get recipient count';
      console.error('Error getting recipient count:', errorMessage);
      throw err;
    }
  };

  const send = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setProgress({ sent: 0, total: 0, status: 'sending' });

      const token = localStorage.getItem('BEARER_TOKEN') || sessionStorage.getItem('BEARER_TOKEN');
      const response = await fetch('/api/emails/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formState),
      });

      if (!response.ok) {
        throw new Error('Failed to send emails');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'start') {
                setProgress({ sent: 0, total: data.total, status: 'sending' });
              } else if (data.type === 'progress') {
                setProgress((prev) => ({ ...prev, sent: data.sent, total: data.total }));
              } else if (data.type === 'complete') {
                setProgress({
                  sent: data.sent,
                  total: data.total,
                  status: 'complete',
                  failed: data.failed,
                  failedEmails: data.failedEmails,
                });
                toast({
                  title: 'Email sent successfully',
                  description: `Sent to ${data.sent} recipients.`,
                });
              }
            } catch {
              // Ignore parse errors
            }
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send email';
      setError(errorMessage);
      setProgress((prev) => ({ ...prev, status: 'error' }));
      toast({
        title: 'Failed to send email',
        description: errorMessage,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const sendTest = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.post('/emails/send-test', formState);
      toast({
        title: 'Test email sent successfully',
        description: 'Check your mail inbox to see how it looks.',
      });
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send test email';
      setError(errorMessage);
      toast({
        title: 'Failed to send test email',
        description: errorMessage,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setError(null);
    setProgress(initialProgress);
    formManager.clear();
  };

  return {
    formManager,
    send,
    sendTest,
    getRecipientCount,
    reset,
    isLoading,
    error,
    progress,
  };
};
